<template>
	<div class="tabs-container">
		<div
			class="tabs"
			:class="{ stretch, left: alignLeft, small, large, buttons }"
		>
			<div
				v-for="(tab, idx) in tabs"
				:key="tab.id || idx"
				class="tab-item"
				:class="{ selected: selected === (tab.id || idx) }"
				tabindex="0"
				@click="changeSelected(tab, idx)"
			>
				<slot :tab="tab" :selected="selected">{{ tab.label || tab }}</slot>
			</div>
		</div>
		<div class="right-content">
			<slot name="rightContent" />
		</div>
	</div>
</template>

<script>
export const sizes = {
	SMALL: 'small',
	LARGE: 'large',
}

export default {
	name: 'Tabs',
	props: {
		tabs: Array,
		// size props are just boolean flags
		...Object.values(sizes).reduce((acc, s) => ({ ...acc, [s]: { type: Boolean, default: null }}), {}),
		alignLeft: { type: Boolean, default: null },
		defaultSelected: [String, Number],
		stretch: { type: Boolean, default: null },
		buttons: { type: Boolean, default: null },
	},

	data() {
		const [first] = this.tabs
		return {
			selected: this.defaultSelected ?? first?.id ?? 0,
		}
	},

	methods: {
		changeSelected(tab, idx) {
			this.selected = tab.id || idx
			this.$emit('change', tab || this.tabs[idx])
		},
	},

	watch: {
		defaultSelected(def) {
			if (def && def !== this.selected) {
				this.selected = def
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.tabs-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-bottom: 1px solid #dfe1e3;

	.tabs {
		flex-grow: 1;
		display: flex;
		align-items: stretch;
		justify-content: space-between;
		width: 100%;
		padding: 0 16px;

		&.left {
			justify-content: flex-start;
			width: 100%;
			gap: 16px;
			padding-left: 0;
		}
		&.stretch {
			width: 100%;
			.tab-item {
				width: 100%;
				justify-content: center;
			}
		}
		&.small {
			.tab-item {
				height: 36px;
			}
		}
		&.large {
			.tab-item {
				height: 60px;
			}
		}
		&.buttons {
			padding: 16px;
			.tab-item {
				border-radius: 6px;
				background: var(--neutral-100);
				border: 1px solid transparent;
				padding: 0 16px;
				color: var(--neutral-700);
				&.selected {
					background: var(--blue-300);
					border-color: var(--blue-300);
					color: white;
				}
				&:not(.selected):hover {
					color: var(--neutral-700);
					background: var(--neutral-200);
				}
			}
		}

		.tab-item {
			@include flex;
			flex: 0 1 auto;
			list-style-type: none;
			text-decoration: none;
			cursor: pointer;
			user-select: none;
			border-bottom: 3px solid transparent;
			color: var(--neutral-800);
			height: 48px; // default size

			&.selected,
			&:hover {
				color: var(--blue-300);
			}

			&.selected {
				border-color: var(--blue-300);
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.tabs-container {
		border-bottom: 1px solid var(--neutral-300);
		.tabs {
			&.buttons {
				.tab-item {
					background: var(--neutral-0);
					color: var(--neutral-800);
					border-color: transparent;
					&:not(.selected):hover {
						color: var(--neutral-800);
						background: var(--neutral-100);
					}
				}
			}
		}
	}
}
</style>
